import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Brain, Sparkles, Loader2, Globe, Zap, Check, X, Trash2, Search,
  Settings2, TrendingUp, Layers, Flag, AlertTriangle,
} from 'lucide-react';
import { api, compact, timeAgo } from '../../lib/api';
import { ARENA } from '../../lib/classTheme';
import { ArenaTitle, ArenaTabs, ArenaEmpty, ArenaStatBar } from '../../components/ArenaFrame';
import { Sheet, SkeletonList } from '../../components/ui';
export default function AdminTrivia() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('generate');
  const [genOpen, setGenOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [previewBatch, setPreviewBatch] = useState(null);
  const { data: overview, isLoading } = useQuery({
    queryKey: ['triviaOverview'],
    queryFn: () => api.get('/trivia/admin/overview').then(r => r.data),
  });
  const { data: health } = useQuery({
    queryKey: ['triviaHealth'],
    queryFn: () => api.get('/trivia/admin/health').then(r => r.data),
    refetchInterval: 60000,
  });
  const { data: aiConfig } = useQuery({
    queryKey: ['triviaAiConfig'],
    queryFn: () => api.get('/trivia/admin/ai-config').then(r => r.data),
  });
  if (isLoading || !overview || !aiConfig) return <SkeletonList n={4} h="h-28" />;
  return (
    <div className="space-y-4">
      {/* ═══ HEADER ═══ */}
      <div className="card-arena p-4 flex items-center gap-3">
        <div className="w-11 h-11 grid place-items-center shrink-0"
          style={{
            borderRadius: 'var(--radius)',
            background: 'var(--accent-soft)',
            border: `1px solid ${ARENA.gold}`,
          }}>
          <Brain size={20} style={{ color: ARENA.arenaText }} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-lg font-extrabold neon-glow-text"
            style={{ color: ARENA.arenaText }}>
            Trivia Studio
          </h2>
          <p className="text-[11px] font-semibold" style={{ color: ARENA.arenaTextSoft }}>
            {compact(overview.totals.active)} active questions · {overview.totals.languages} languages
          </p>
        </div>
        <HealthBadge health={health} />
      </div>
      {/* ═══ STATS ═══ */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <Stat v={compact(overview.totals.total)} l="Total Q's" />
        <Stat v={compact(overview.totals.active || 0)} l="Active" hi />
        <Stat v={overview.totals.languages} l="Languages" />
        <Stat v={overview.totals.categories} l="Categories" />
        <Stat v={compact(overview.totals.times_used)} l="Answered" />
        <Stat v={overview.totals.times_used
          ? `${Math.round(overview.totals.times_correct / overview.totals.times_used * 100)}%`
          : '—'} l="Accuracy" />
      </div>
      {overview.totals.reported > 0 && (
        <div className="card p-3 flex items-center gap-3" style={{ borderColor: '#f59e0b', borderWidth: 2 }}>
          <AlertTriangle size={18} style={{ color: '#f59e0b' }} className="shrink-0" />
          <p className="text-sm font-semibold">
            <b>{overview.totals.reported}</b> question{overview.totals.reported !== 1 ? 's' : ''} reported by players — review in Question Bank.
          </p>
        </div>
      )}
      <ArenaTabs value={tab} onChange={setTab} tabs={[
        { key: 'generate', label: 'Generate', icon: Sparkles },
        { key: 'bank', label: 'Question Bank', icon: Layers },
        { key: 'groups', label: 'City Settings', icon: Globe },
      ]} />
      {/* ═══════════ GENERATE ═══════════ */}
      {tab === 'generate' && (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <button onClick={() => setGenOpen(true)}
              className="card p-5 text-left transition hover:scale-[1.01]"
              style={{ borderColor: ARENA.gold, borderWidth: 2 }}>
              <Sparkles size={22} style={{ color: ARENA.goldD }} className="mb-2" />
              <p className="font-display font-bold">Generate single batch</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                Pick model, language, category, difficulty and count.
              </p>
            </button>
            <button onClick={() => setBulkOpen(true)}
              className="card p-5 text-left transition hover:scale-[1.01]">
              <Globe size={22} style={{ color: 'var(--text-soft)' }} className="mb-2" />
              <p className="font-display font-bold">Generate for multiple languages</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                Same settings, run across up to 10 languages at once.
              </p>
            </button>
          </div>
          {overview.byLanguage.length > 0 && (
            <>
              <ArenaTitle title="By language" latin="Per Linguas" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {overview.byLanguage.map(l => (
                  <div key={l.language} className="card p-3">
                    <p className="font-display font-black text-lg">{l.language.toUpperCase()}</p>
                    <p className="text-xs font-bold" style={{ color: ARENA.goldD }}>
                      {l.active} active
                    </p>
                    <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                      {l.ai_generated} AI · {l.manual} manual
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
          {!overview.byLanguage.length && (
            <ArenaEmpty emoji="🌐" title="No questions yet"
              sub="Generate your first batch to get started."
              action={<button onClick={() => setGenOpen(true)} className="btn-primary btn-sm">
                <Sparkles size={13} /> Generate now</button>} />
          )}
          {overview.byCategory.length > 0 && (
            <>
              <ArenaTitle title="By category" latin="Per Categorias" />
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {overview.byCategory.map(c => (
                  <div key={c.category} className="card p-3 text-center">
                    <p className="font-display font-bold text-xs truncate capitalize">
                      {c.category.replace('_', ' ')}
                    </p>
                    <p className="text-lg font-black" style={{ color: ARENA.goldD }}>{c.active}</p>
                  </div>
                ))}
              </div>
            </>
          )}
          <ArenaTitle title="Recent batches" latin="Nuper" />
          <div className="card p-2 space-y-1">
            {overview.recentBatches.map(b => (
              <button key={b.id} onClick={() => setPreviewBatch(b)}
                className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-black/5 text-left transition">
                <div className="w-9 h-9 rounded-xl grid place-items-center text-white shrink-0"
                  style={{
                    background: b.status === 'done' ? '#10b981'
                      : b.status === 'running' ? ARENA.gold
                      : b.status === 'failed' ? '#ef4444' : '#94a3b8',
                  }}>
                  {b.status === 'done' ? <Check size={16} />
                    : b.status === 'running' ? <Loader2 size={16} className="animate-spin" />
                    : b.status === 'failed' ? <X size={16} /> : <Brain size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-sm truncate">
                    {b.language.toUpperCase()} · {b.category} · {b.difficulty}
                  </p>
                  <p className="text-[11px] font-semibold" style={{ color: 'var(--text-muted)' }}>
                    {b.model} · {timeAgo(b.created_at)}
                    {b.created_by_name && ` · @${b.created_by_name}`}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display font-black text-sm">
                    {b.count_created}/{b.count_requested}
                  </p>
                  {b.count_failed > 0 && (
                    <p className="text-[10px] font-bold text-red-500">{b.count_failed} failed</p>
                  )}
                </div>
              </button>
            ))}
            {!overview.recentBatches.length && (
              <p className="text-center text-sm py-6" style={{ color: 'var(--text-muted)' }}>
                No batches yet
              </p>
            )}
          </div>
        </>
      )}
      {/* ═══════════ QUESTION BANK ═══════════ */}
      {tab === 'bank' && <QuestionBank />}
      {/* ═══════════ CITY SETTINGS ═══════════ */}
      {tab === 'groups' && <GroupSettings />}
      {/* ═══ SHEETS ═══ */}
      <GenerateSheet open={genOpen} onClose={() => setGenOpen(false)}
        aiConfig={aiConfig}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ['triviaOverview'] });
          qc.invalidateQueries({ queryKey: ['triviaQuestions'] });
        }} />
      <BulkGenerateSheet open={bulkOpen} onClose={() => setBulkOpen(false)}
        aiConfig={aiConfig}
        onDone={() => qc.invalidateQueries({ queryKey: ['triviaOverview'] })} />
      <BatchPreviewSheet batch={previewBatch} onClose={() => setPreviewBatch(null)} />
    </div>
  );
}
/* ============================================================
   HEALTH BADGE
   ============================================================ */
function HealthBadge({ health }) {
  if (!health) return null;
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold shrink-0"
      style={{
        background: health.ok ? 'rgba(16,185,129,.14)' : 'rgba(239,68,68,.14)',
        color: health.ok ? '#059669' : '#dc2626',
      }}>
      <span className="w-1.5 h-1.5 rounded-full"
        style={{ background: health.ok ? '#10b981' : '#ef4444' }} />
      {health.ok ? 'AI Online' : health.error || 'AI Offline'}
    </div>
  );
}
/* ============================================================
   GENERATE SHEET (single batch)
   ============================================================ */
function GenerateSheet({ open, onClose, aiConfig, onDone }) {
  const [model, setModel] = useState('openai/gpt-5.4-mini');
  const [language, setLanguage] = useState('en');
  const [category, setCategory] = useState('general');
  const [difficulty, setDifficulty] = useState('medium');
  const [count, setCount] = useState(20);
  useEffect(() => {
    if (aiConfig?.default_model) setModel(aiConfig.default_model);
    if (aiConfig?.default_language) setLanguage(aiConfig.default_language);
    if (aiConfig?.default_category) setCategory(aiConfig.default_category);
    if (aiConfig?.default_difficulty) setDifficulty(aiConfig.default_difficulty);
    if (aiConfig?.default_count) setCount(aiConfig.default_count);
  }, [aiConfig]);
  const { data: languages } = useQuery({
    queryKey: ['triviaLanguages'],
    queryFn: () => api.get('/trivia/languages').then(r => r.data),
    enabled: open,
  });
  const gen = useMutation({
    mutationFn: () => api.post('/trivia/admin/generate', { model, language, category, difficulty, count }),
    onSuccess: ({ data: d }) => {
      toast.success(d.message, { duration: 6000 });
      if (d.failed > 0) console.warn('Invalid questions:', d.invalidDetails);
      onClose();
      onDone();
    },
    onError: e => toast.error(e.message),
  });
  const estCost = Math.round(count * (model.includes('nano') ? 0.001 : 0.004) * 1000) / 1000;
  return (
    <Sheet open={open} onClose={onClose} title="Generate trivia questions">
      <div className="space-y-4">
        <div>
          <label className="label">AI Model</label>
          <div className="grid grid-cols-2 gap-2">
            {(aiConfig?.models || [
              { key: 'openai/gpt-5.4-mini', label: 'GPT-5.4 Mini', tier: 'quality' },
              { key: 'openai/gpt-5.4-nano', label: 'GPT-5.4 Nano', tier: 'fast' },
            ]).map(m => (
              <button key={m.key} onClick={() => setModel(m.key)}
                className="p-3 rounded-xl border-2 text-left transition"
                style={model === m.key
                  ? { borderColor: ARENA.gold, background: 'var(--accent-soft)' }
                  : { borderColor: 'var(--border)', background: 'var(--surface)' }}>
                <p className="font-display font-bold text-sm">{m.label}</p>
                <p className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>
                  {m.tier === 'quality' ? '🎯 Higher quality' : '⚡ Fast & cheap'}
                </p>
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="label">Language</label>
          <select className="input" value={language} onChange={e => setLanguage(e.target.value)}>
            {(languages || [{ code: 'en', name: 'English', flag: '🇬🇧' }]).map(l => (
              <option key={l.code} value={l.code}>{l.flag} {l.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Category</label>
          <select className="input" value={category} onChange={e => setCategory(e.target.value)}>
            {(aiConfig?.categories || ['general']).map(c => (
              <option key={c} value={c}>{c.replace('_', ' ')}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Difficulty</label>
          <div className="flex gap-2">
            {['easy', 'medium', 'hard'].map(d => (
              <button key={d} onClick={() => setDifficulty(d)}
                className="flex-1 py-2 rounded-lg text-sm font-bold capitalize transition"
                style={difficulty === d
                  ? { background: ARENA.goldFrame, color: '#fff' }
                  : { background: 'var(--surface-alt)', color: 'var(--text-muted)' }}>
                {d}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="flex justify-between mb-1">
            <label className="label !mb-0">Count</label>
            <span className="font-display font-black" style={{ color: ARENA.goldD }}>{count}</span>
          </div>
          <input type="range" min="5" max={aiConfig?.max_count_per_batch || 50} step="5"
            value={count} onChange={e => setCount(Number(e.target.value))}
            className="w-full" style={{ accentColor: ARENA.gold }} />
        </div>
        <div className="p-3 rounded-xl" style={{ background: 'var(--accent-soft)' }}>
          <p className="text-[11px] font-bold" style={{ color: ARENA.goldD }}>
            💰 Estimated cost: ~${estCost} · ⏱ ~{Math.ceil(count / 10) * 3}s
          </p>
        </div>
        <button onClick={() => gen.mutate()} disabled={gen.isPending} className="btn-primary w-full py-3">
          {gen.isPending
            ? <><Loader2 size={16} className="animate-spin" /> Generating {count} questions…</>
            : <><Sparkles size={16} /> Generate {count} questions</>}
        </button>
      </div>
    </Sheet>
  );
}
/* ============================================================
   BULK GENERATE SHEET
   ============================================================ */
function BulkGenerateSheet({ open, onClose, aiConfig, onDone }) {
  const [model, setModel] = useState('openai/gpt-5.4-mini');
  const [category, setCategory] = useState('general');
  const [difficulty, setDifficulty] = useState('medium');
  const [count, setCount] = useState(15);
  const [selected, setSelected] = useState(['ro', 'de', 'fr', 'es', 'it']);
  useEffect(() => {
    if (aiConfig?.default_model) setModel(aiConfig.default_model);
  }, [aiConfig]);
  const { data: languages } = useQuery({
    queryKey: ['triviaLanguages'],
    queryFn: () => api.get('/trivia/languages').then(r => r.data),
    enabled: open,
  });
  const gen = useMutation({
    mutationFn: () => api.post('/trivia/admin/generate-bulk', {
      model, category, difficulty, count, languages: selected,
    }),
    onSuccess: ({ data: d }) => {
      const ok = d.results.filter(r => r.ok).length;
      toast.success(`✅ ${d.totalCreated} questions across ${ok}/${d.results.length} languages`, { duration: 7000 });
      onClose();
      onDone();
    },
    onError: e => toast.error(e.message),
  });
  const toggle = (code) => setSelected(s => s.includes(code) ? s.filter(x => x !== code) : [...s, code]);
  return (
    <Sheet open={open} onClose={onClose} title="Generate for multiple languages" wide>
      <div className="space-y-4">
        <div>
          <label className="label">Languages ({selected.length}/10 max)</label>
          <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
            {(languages || []).map(l => (
              <button key={l.code} onClick={() => toggle(l.code)}
                disabled={!selected.includes(l.code) && selected.length >= 10}
                className="px-2.5 py-1.5 rounded-lg text-xs font-bold transition"
                style={selected.includes(l.code)
                  ? { background: ARENA.goldFrame, color: '#fff' }
                  : { background: 'var(--surface-alt)', color: 'var(--text-muted)' }}>
                {l.flag} {l.name}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Model</label>
            <select className="input" value={model} onChange={e => setModel(e.target.value)}>
              {(aiConfig?.models || [
                { key: 'openai/gpt-5.4-mini', label: 'GPT-5.4 Mini' },
                { key: 'openai/gpt-5.4-nano', label: 'GPT-5.4 Nano' },
              ]).map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Category</label>
            <select className="input" value={category} onChange={e => setCategory(e.target.value)}>
              {(aiConfig?.categories || ['general']).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Difficulty</label>
            <select className="input" value={difficulty} onChange={e => setDifficulty(e.target.value)}>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
          <div>
            <label className="label">Count per language</label>
            <input type="number" className="input" min={5} max={50} value={count}
              onChange={e => setCount(Number(e.target.value))} />
          </div>
        </div>
        <div className="p-3 rounded-xl" style={{ background: 'var(--accent-soft)' }}>
          <p className="text-[11px] font-bold" style={{ color: ARENA.goldD }}>
            Total: {selected.length * count} questions across {selected.length} languages
          </p>
        </div>
        <button onClick={() => gen.mutate()} disabled={gen.isPending || !selected.length}
          className="btn-primary w-full py-3">
          {gen.isPending
            ? <><Loader2 size={16} className="animate-spin" /> Generating…</>
            : <><Globe size={16} /> Generate for {selected.length} languages</>}
        </button>
      </div>
    </Sheet>
  );
}
/* ============================================================
   QUESTION BANK
   ============================================================ */
function QuestionBank() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [langFilter, setLangFilter] = useState('');
  const [reportedOnly, setReportedOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const { data, isLoading } = useQuery({
    queryKey: ['triviaQuestions', search, langFilter, reportedOnly],
    queryFn: () => api.get('/trivia/admin/questions', {
      params: {
        q: search || undefined,
        language: langFilter || undefined,
        reported: reportedOnly ? '1' : undefined,
        limit: 60,
      },
    }).then(r => r.data),
  });
  const del = useMutation({
    mutationFn: (ids) => api.post('/trivia/admin/questions/bulk-delete', { ids }),
    onSuccess: ({ data: d }) => {
      toast.success(`${d.deleted} questions deleted`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['triviaQuestions'] });
      qc.invalidateQueries({ queryKey: ['triviaOverview'] });
    },
  });
  const patch = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/trivia/admin/questions/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['triviaQuestions'] }),
  });
  const toggle = (id) => setSelected(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--text-muted)' }} />
          <input className="input pl-9" placeholder="Search questions…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <input className="input w-20" placeholder="lang" value={langFilter}
          onChange={e => setLangFilter(e.target.value.toLowerCase())} maxLength={2} />
        <button onClick={() => setReportedOnly(v => !v)}
          className="btn-sm px-3 rounded-lg font-bold text-xs shrink-0"
          style={reportedOnly
            ? { background: '#f59e0b', color: '#fff' }
            : { background: 'var(--surface-alt)', color: 'var(--text-muted)' }}>
          <Flag size={12} className="inline mr-1" /> Reported
        </button>
      </div>
      {selected.size > 0 && (
        <div className="flex items-center gap-2 p-2 rounded-xl" style={{ background: 'var(--accent-soft)' }}>
          <span className="text-sm font-bold">{selected.size} selected</span>
          <button onClick={() => del.mutate([...selected])} disabled={del.isPending}
            className="btn btn-sm ml-auto" style={{ background: '#ef4444', color: '#fff' }}>
            <Trash2 size={12} /> Delete
          </button>
        </div>
      )}
      {isLoading ? <SkeletonList n={6} h="h-20" />
        : !data?.questions.length ? <ArenaEmpty emoji="🔍" title="No questions found" />
        : (
          <div className="space-y-2">
            {data.questions.map(qz => (
              <div key={qz.id} className="card p-3 flex gap-3"
                style={qz.reported > 0 ? { borderColor: '#f59e0b', borderWidth: 2 } : {}}>
                <input type="checkbox" checked={selected.has(qz.id)} onChange={() => toggle(qz.id)}
                  className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    <span className="chip-ink">{qz.language.toUpperCase()}</span>
                    <span className="chip-ink">{qz.category}</span>
                    <span className="chip-ink">{qz.difficulty}</span>
                    {qz.source === 'ai_generated' && <span className="chip-brand">AI</span>}
                    {qz.reported > 0 && (
                      <span className="chip" style={{ background: '#fef3c7', color: '#92400e' }}>
                        <Flag size={9} /> {qz.reported}
                      </span>
                    )}
                    {qz.status === 'hidden' && <span className="chip" style={{ background: '#f1f5f9', color: '#64748b' }}>Hidden</span>}
                  </div>
                  <p className="font-semibold text-sm">{qz.question}</p>
                  <div className="grid grid-cols-2 gap-1 mt-1.5">
                    {qz.options.map((o, i) => (
                      <span key={i} className="text-[11px] px-2 py-1 rounded"
                        style={{
                          background: i === qz.correct_index ? 'rgba(16,185,129,.15)' : 'var(--surface-alt)',
                          color: i === qz.correct_index ? '#059669' : 'var(--text-muted)',
                          fontWeight: i === qz.correct_index ? 700 : 400,
                        }}>
                        {i === qz.correct_index ? '✓ ' : ''}{o}
                      </span>
                    ))}
                  </div>
                  {qz.explanation && (
                    <p className="text-[11px] mt-1 italic" style={{ color: 'var(--text-muted)' }}>
                      {qz.explanation}
                    </p>
                  )}
                  <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                    Used {qz.times_used}× · {qz.times_used ? Math.round(qz.times_correct/qz.times_used*100) : 0}% correct
                  </p>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {qz.status === 'active' ? (
                    <button onClick={() => patch.mutate({ id: qz.id, body: { status: 'hidden' } })}
                      className="p-1.5 rounded-lg" style={{ color: 'var(--text-muted)' }} title="Hide">
                      <X size={14} />
                    </button>
                  ) : (
                    <button onClick={() => patch.mutate({ id: qz.id, body: { status: 'active' } })}
                      className="p-1.5 rounded-lg" style={{ color: '#059669' }} title="Restore">
                      <Check size={14} />
                    </button>
                  )}
                  <button onClick={() => del.mutate([qz.id])}
                    className="p-1.5 rounded-lg" style={{ color: '#ef4444' }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      {data && <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
        Showing {data.questions.length} of {data.total}
      </p>}
    </div>
  );
}
/* ============================================================
   GROUP SETTINGS
   ============================================================ */
function GroupSettings() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [editGlobal, setEditGlobal] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['triviaConfigs', search],
    queryFn: () => api.get('/trivia/admin/configs', { params: { q: search || undefined } }).then(r => r.data),
  });
  const toggle = useMutation({
    mutationFn: ({ groupId, enabled }) => api.post('/trivia/admin/configs', { groupId, enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['triviaConfigs'] }),
  });
  if (isLoading || !data) return <SkeletonList n={6} h="h-16" />;
  return (
    <div className="space-y-3">
      <button onClick={() => setEditGlobal(true)}
        className="card p-4 w-full text-left flex items-center gap-3 transition hover:scale-[1.01]"
        style={{ borderColor: ARENA.gold, borderWidth: 2 }}>
        <Settings2 size={20} style={{ color: ARENA.goldD }} className="shrink-0" />
        <div className="flex-1">
          <p className="font-display font-bold text-sm">Global default settings</p>
          <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
            {data.global.enabled ? '✓ Enabled' : '✗ Disabled'} · {data.global.questions_per_round} questions/round ·
            {' '}{data.global.points_per_correct}pts/correct
          </p>
        </div>
      </button>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2"
          style={{ color: 'var(--text-muted)' }} />
        <input className="input pl-9" placeholder="Search city or country…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <div className="space-y-2">
        {data.groups.map(g => {
          const hasOverride = !!g.config_id;
          const isEnabled = hasOverride ? !!g.enabled : data.global.enabled;
          return (
            <div key={g.id} className="card p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-display font-bold text-sm">{g.handle}</p>
                  {hasOverride && <span className="chip-brand !text-[8px]">CUSTOM</span>}
                </div>
                <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  {g.name} · {hasOverride ? (g.language || 'auto') : 'using global'}
                  {g.sponsor_name && ` · sponsor: ${g.sponsor_name}`}
                </p>
              </div>
              <button onClick={() => toggle.mutate({ groupId: g.id, enabled: !isEnabled })}
                className="w-11 h-6 rounded-full transition relative shrink-0"
                style={{ background: isEnabled ? ARENA.goldFrame : 'var(--border-strong)' }}>
                <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
                  style={{ left: isEnabled ? 22 : 2 }} />
              </button>
              <button onClick={() => setEditing(g)} className="btn-ghost btn-sm shrink-0">
                <Settings2 size={12} />
              </button>
            </div>
          );
        })}
      </div>
      <EditGroupSheet group={editing} onClose={() => setEditing(null)} globalDefaults={data.global} />
      <EditGlobalSheet open={editGlobal} onClose={() => setEditGlobal(false)} config={data.global} />
    </div>
  );
}
function EditGroupSheet({ group, onClose, globalDefaults }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({});
  useEffect(() => {
    if (group) {
      setForm({
        language: group.language || '',
        difficulty: group.difficulty || globalDefaults.difficulty,
        questions_per_round: group.questions_per_round ?? globalDefaults.questions_per_round,
        seconds_per_question: group.seconds_per_question ?? globalDefaults.seconds_per_question,
        points_per_correct: group.points_per_correct ?? globalDefaults.points_per_correct,
        perfect_bonus: group.perfect_bonus ?? globalDefaults.perfect_bonus,
        daily_rounds_limit: group.daily_rounds_limit ?? globalDefaults.daily_rounds_limit,
        live_enabled: group.live_enabled !== 0,
      });
    }
  }, [group, globalDefaults]);
  const save = useMutation({
    mutationFn: () => api.post('/trivia/admin/configs', { groupId: group.id, ...form }),
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({ queryKey: ['triviaConfigs'] });
      onClose();
    },
    onError: e => toast.error(e.message),
  });
  const reset = useMutation({
    mutationFn: () => api.delete(`/trivia/admin/configs/group/${group.id}`),
    onSuccess: () => {
      toast.success('Reset to global defaults');
      qc.invalidateQueries({ queryKey: ['triviaConfigs'] });
      onClose();
    },
  });
  if (!group) return null;
  return (
    <Sheet open={!!group} onClose={onClose} title={`Configure ${group.handle}`}>
      <div className="space-y-3">
        <div>
          <label className="label">Force language (empty = auto-detect)</label>
          <input className="input" value={form.language || ''} maxLength={2}
            placeholder="e.g. ro, de, fr — leave empty for auto"
            onChange={e => setForm({ ...form, language: e.target.value.toLowerCase() })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Questions/round</label>
            <input type="number" className="input" value={form.questions_per_round ?? 10}
              onChange={e => setForm({ ...form, questions_per_round: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Seconds/question</label>
            <input type="number" className="input" value={form.seconds_per_question ?? 15}
              onChange={e => setForm({ ...form, seconds_per_question: Number(e.target.value) })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Points per correct</label>
            <input type="number" className="input" value={form.points_per_correct ?? 10}
              onChange={e => setForm({ ...form, points_per_correct: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Daily rounds limit</label>
            <input type="number" className="input" value={form.daily_rounds_limit ?? 3}
              onChange={e => setForm({ ...form, daily_rounds_limit: Number(e.target.value) })} />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm font-bold">
          <input type="checkbox" checked={form.live_enabled !== false}
            onChange={e => setForm({ ...form, live_enabled: e.target.checked })}
            className="w-4 h-4" style={{ accentColor: ARENA.gold }} />
          Live chat trivia enabled (!trivia command)
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => reset.mutate()} disabled={reset.isPending} className="btn-ghost">
            Reset to global
          </button>
          <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary">
            {save.isPending ? <Loader2 size={15} className="animate-spin" /> : 'Save'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
function EditGlobalSheet({ open, onClose, config }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({});
  useEffect(() => {
    if (config) {
      setForm({
        enabled: !!config.enabled,
        live_enabled: !!config.live_enabled,
        live_questions: config.live_questions,
        live_seconds: config.live_seconds,
        live_points_per_correct: config.live_points_per_correct,
        live_first_bonus: config.live_first_bonus,
        questions_per_round: config.questions_per_round,
        seconds_per_question: config.seconds_per_question,
        points_per_correct: config.points_per_correct,
        perfect_bonus: config.perfect_bonus,
        streak_bonus: config.streak_bonus,
        xp_per_correct: config.xp_per_correct,
        daily_rounds_limit: config.daily_rounds_limit,
      });
    }
  }, [config]);
  const save = useMutation({
    mutationFn: () => api.post('/trivia/admin/configs', form),
    onSuccess: () => {
      toast.success('Global settings saved');
      qc.invalidateQueries({ queryKey: ['triviaConfigs'] });
      onClose();
    },
    onError: e => toast.error(e.message),
  });
  return (
    <Sheet open={open} onClose={onClose} title="Global trivia settings" wide>
      <div className="space-y-4">
        <label className="flex items-center gap-2 text-sm font-bold">
          <input type="checkbox" checked={!!form.enabled}
            onChange={e => setForm({ ...form, enabled: e.target.checked })}
            className="w-4 h-4" style={{ accentColor: ARENA.gold }} />
          Trivia enabled platform-wide (default for all cities)
        </label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Questions per round</label>
            <input type="number" className="input" value={form.questions_per_round ?? 10}
              onChange={e => setForm({ ...form, questions_per_round: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Seconds per question</label>
            <input type="number" className="input" value={form.seconds_per_question ?? 20}
              onChange={e => setForm({ ...form, seconds_per_question: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Points per correct</label>
            <input type="number" className="input" value={form.points_per_correct ?? 10}
              onChange={e => setForm({ ...form, points_per_correct: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Perfect round bonus</label>
            <input type="number" className="input" value={form.perfect_bonus ?? 100}
              onChange={e => setForm({ ...form, perfect_bonus: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">XP per correct</label>
            <input type="number" className="input" value={form.xp_per_correct ?? 15}
              onChange={e => setForm({ ...form, xp_per_correct: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Daily rounds limit</label>
            <input type="number" className="input" value={form.daily_rounds_limit ?? 3}
              onChange={e => setForm({ ...form, daily_rounds_limit: Number(e.target.value) })} />
          </div>
        </div>
        <div className="divider" />
        <p className="label">Live chat trivia (!trivia command)</p>
        <label className="flex items-center gap-2 text-sm font-bold">
          <input type="checkbox" checked={!!form.live_enabled}
            onChange={e => setForm({ ...form, live_enabled: e.target.checked })}
            className="w-4 h-4" style={{ accentColor: ARENA.gold }} />
          Live trivia enabled by default
        </label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Questions per round</label>
            <input type="number" className="input" value={form.live_questions ?? 5}
              onChange={e => setForm({ ...form, live_questions: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Seconds per question</label>
            <input type="number" className="input" value={form.live_seconds ?? 15}
              onChange={e => setForm({ ...form, live_seconds: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Points per correct</label>
            <input type="number" className="input" value={form.live_points_per_correct ?? 12}
              onChange={e => setForm({ ...form, live_points_per_correct: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">First-answer bonus</label>
            <input type="number" className="input" value={form.live_first_bonus ?? 15}
              onChange={e => setForm({ ...form, live_first_bonus: Number(e.target.value) })} />
          </div>
        </div>
        <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary w-full">
          {save.isPending ? <Loader2 size={15} className="animate-spin" /> : 'Save global settings'}
        </button>
      </div>
    </Sheet>
  );
}
/* ============================================================
   BATCH PREVIEW
   ============================================================ */
function BatchPreviewSheet({ batch, onClose }) {
  const { data } = useQuery({
    queryKey: ['triviaBatchDetail', batch?.id],
    queryFn: () => api.get(`/trivia/admin/batches/${batch.id}`).then(r => r.data),
    enabled: !!batch,
  });
  if (!batch) return null;
  return (
    <Sheet open={!!batch} onClose={onClose} title={`Batch ${batch.code}`} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <MetaStat l="Requested" v={batch.count_requested} />
          <MetaStat l="Created" v={batch.count_created} hi />
          <MetaStat l="Failed" v={batch.count_failed} danger={batch.count_failed > 0} />
        </div>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {batch.model} · {batch.language.toUpperCase()} · {batch.category} · {batch.difficulty}
        </p>
        {!data ? <SkeletonList n={3} h="h-20" /> : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {data.questions.map(qz => (
              <div key={qz.id} className="card p-3">
                <p className="font-semibold text-sm">{qz.question}</p>
                <div className="grid grid-cols-2 gap-1 mt-1.5">
                  {qz.options.map((o, i) => (
                    <span key={i} className="text-[11px] px-2 py-1 rounded"
                      style={{
                        background: i === qz.correct_index ? 'rgba(16,185,129,.15)' : 'var(--surface-alt)',
                        color: i === qz.correct_index ? '#059669' : 'var(--text-muted)',
                      }}>
                      {i === qz.correct_index ? '✓ ' : ''}{o}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Sheet>
  );
}
/* ============================================================
   BITS
   ============================================================ */
function Stat({ v, l, hi }) {
  return (
    <div className="card p-2.5 text-center">
      <p className="font-display font-black text-base"
        style={{ color: hi ? ARENA.goldD : 'var(--text)' }}>{v}</p>
      <p className="text-[9px] font-display font-bold uppercase" style={{ color: 'var(--text-muted)' }}>{l}</p>
    </div>
  );
}
function MetaStat({ l, v, hi, danger }) {
  return (
    <div className="rounded-xl p-2 text-center" style={{ background: 'var(--surface-alt)' }}>
      <p className="text-[9px] font-display font-black uppercase" style={{ color: 'var(--text-muted)' }}>{l}</p>
      <p className="font-display font-black text-lg mt-0.5"
        style={{ color: danger ? '#ef4444' : hi ? ARENA.goldD : 'var(--text)' }}>{v}</p>
    </div>
  );
}